import { useQuery } from "@tanstack/react-query";
import { useSession } from "@supabase/auth-helpers-react";
import { useImpersonation } from "@providers/ImpersonationProvider";
import { fetchGroupMe } from "@common/lib/groupUsageApi";

// Resolves whether the current session belongs to a corporate account group.
// Retry policy is status-aware: a 401 means "not a group user" (the common case),
// NOT a transient failure — retrying it would spam an expected outcome. A 5xx,
// though, means the group lookup itself failed; retrying protects a real group
// admin from being silently dropped into the (broken) clinic view during a
// transient DB blip, since PermissionWrapper only routes to /corporate on a
// resolved group. See groupUsageController's deliberate 401-vs-500 split.
export function useGroupSession() {
    const session = useSession();
    const { impersonationSession, isImpersonating } = useImpersonation();
    const effectiveUserId =
        isImpersonating && impersonationSession?.targetUserId ? impersonationSession.targetUserId : session?.user?.id;

    return useQuery({
        // Keyed on the effective user id so a sign-out/sign-in (or impersonation
        // switch) without a full reload can't serve a different user's cached
        // group membership from this query's gcTime window.
        queryKey: ["group-me", effectiveUserId],
        queryFn: fetchGroupMe,
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        retry: (failureCount, error) => {
            const status = (error as { response?: { status?: number } })?.response?.status;
            // Retry only server errors (5xx / no status), never an expected 401/4xx.
            const isServerError = typeof status !== "number" || status >= 500;
            return isServerError && failureCount < 2;
        },
        enabled: Boolean(effectiveUserId),
    });
}
